import axios from "../../../../../pages/api/axios";
import { toast } from 'react-toastify';

/**
 * API endpoints for Recurring Inventory Item operations
 */
export const RECURRING_INVENTORY_ENDPOINTS = {
  BASE: "/v2/inventory/recurring-inventory-item",
  CREATE: "/v2/inventory/recurring-inventory-item",
  GET_ALL: "/v2/inventory/recurring-inventory-item",
  UPDATE: "/v2/inventory/recurring-inventory-item",
  DELETE: "/v2/inventory/recurring-inventory-item/:id",
  UPLOAD_INVOICE: "/v2/inventory/recurring-inventory-item/upload-invoice",
  GET_INVOICE_SIGNED_URL: "/v2/inventory/recurring-inventory-item/invoice-signed-url",
  MOVE_ITEM: "/v2/inventory/recurring-inventory-item/move",
  GENERATE_REPORT: "/v2/inventory/recurring-inventory-item/generate-report",
  ADD_TO_VENDOR: "/v2/inventory/recurring-inventory-item/add-to-vendor",
};

/**
 * Centralized error handler for API calls
 * @param {Error} error - Axios error object
 * @param {string} defaultMessage - Fallback message
 */
const handleApiError = (error, defaultMessage) => {
  const message =
    error.response?.data?.message ||
    error.message ||
    defaultMessage ||
    "An unexpected error occurred";
  console.error(defaultMessage, error);
  toast.error(message);
  throw new Error(message);
};

/**
 * Create one or more recurring inventory items
 * @param {Object} payload - { property_id, building_name, items: [...] }
 * @returns {Promise<Object>} Response with created items, inventory_id, errors if any
 */
export const createRecurringInventoryItems = async (payload) => {
  try {
    const response = await axios.post(RECURRING_INVENTORY_ENDPOINTS.CREATE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to create recurring inventory items");
  }
};

/**
 * Update an existing recurring inventory item
 * @param {Object} payload - { item_id, item: { name?, make?, modelNo?, ... } }
 * @returns {Promise<Object>} Updated item object
 */
export const updateRecurringInventoryItem = async (payload) => {
  try {
    const response = await axios.put(RECURRING_INVENTORY_ENDPOINTS.UPDATE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to update recurring inventory item");
  }
};

/**
 * Delete a recurring inventory item by ID
 * @param {string} itemId 
 * @returns {Promise<Object>} Deletion result
 */
export const deleteRecurringInventoryItem = async (itemId) => {
  try {
    const url = RECURRING_INVENTORY_ENDPOINTS.DELETE.replace(":id", itemId);
    const response = await axios.delete(url);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to delete recurring inventory item");
  }
};

/**
 * Get all recurring inventory items for a property and building
 * @param {string} propertyId 
 * @param {string} buildingName 
 * @returns {Promise<{ success: boolean, inventory: Object|null, items: Array }>}
 */
export const getRecurringInventoryItems = async (propertyId, buildingName) => {
  try {
    const response = await axios.get(RECURRING_INVENTORY_ENDPOINTS.GET_ALL, {
      params: { property_id: propertyId, building_name: buildingName },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch recurring inventory items");
  }
};

/**
 * Upload an invoice file (multipart/form-data)
 * @param {File} file - The invoice file (PDF, image, etc.)
 * @param {string} propertyId 
 * @returns {Promise<{ success: boolean, message: string, fileUrl: string }>}
 */
export const uploadRecurringInventoryInvoice = async (file, propertyId) => {
  try {
    const formData = new FormData();
    formData.append("invoice", file);

    const response = await axios.post(
      RECURRING_INVENTORY_ENDPOINTS.UPLOAD_INVOICE,
      formData,
      {
        params: { property_id: propertyId },
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to upload invoice");
  }
};

/**
 * Get a temporary signed URL to view/download an invoice
 * @param {string} fileUrl - The original S3 URL of the invoice
 * @returns {Promise<{ success: boolean, data: { file_url: string, signed_url: string, expires_in: number } }>}
 */
export const getRecurringInventoryInvoiceSignedUrl = async (fileUrl) => {
  try {
    const response = await axios.get(RECURRING_INVENTORY_ENDPOINTS.GET_INVOICE_SIGNED_URL, {
      params: { file_url: fileUrl },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to get signed URL for invoice");
  }
};

/**
 * Move (or split quantity of) a recurring inventory item to another property/building
 * @param {Object} payload - { item_id, destination_property_id, destination_building_name, quantity? }
 * @returns {Promise<Object>} Moved/updated item info
 */
export const moveRecurringInventoryItem = async (payload) => {
  try {
    const response = await axios.post(RECURRING_INVENTORY_ENDPOINTS.MOVE_ITEM, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to move recurring inventory item");
  }
};

/**
 * Generate and download Excel report of recurring inventory items
 * @param {Object} payload - Filter criteria (property_id, building_name, itemName?, modelNo?, requestType?, location?)
 * @returns {Promise<void>} Triggers file download
 */
export const generateRecurringInventoryReport = async (payload) => {
  if (!payload.property_id || !payload.building_name) {
    toast.error("Property ID and building name are required for report");
    return;
  }

  try {
    toast.info("Generating inventory report...");

    const response = await axios.post(
      RECURRING_INVENTORY_ENDPOINTS.GENERATE_REPORT,
      payload,
      { responseType: "blob" }
    );

    const contentDisposition = response.headers["content-disposition"];
    let fileName = `Recurring_Inventory_Report_${new Date().toISOString().split("T")[0]}.xlsx`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match?.[1]) fileName = match[1];
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success("Report downloaded successfully");
  } catch (error) {
    handleApiError(error, "Failed to generate recurring inventory report");
  }
};

/**
 * Add a recurring inventory item to a vendor's catalog
 * @param {Object} payload - { vendor_id, inventory_item_id }
 * @returns {Promise<Object>} Updated vendor with item added
 */
export const addRecurringInventoryItemToVendor = async (payload) => {
  try {
    const response = await axios.post(RECURRING_INVENTORY_ENDPOINTS.ADD_TO_VENDOR, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to add item to vendor");
  }
};